import { boot } from "quasar/wrappers"
import { showDragAndDropEmptyFiles, showDragAndDropFail } from "src/util/functions/notifications"
import { v4 as uuid4 } from "uuid"
import { DirectiveBinding, Ref, ref, VNode } from "vue"

export default boot(({ app }) => {
  app.directive(TRUNCATE_MIDDLE_DIRECTIVE, {
    mounted: truncateMiddleMount,
    beforeUpdate: truncateMiddleBeforeUpdate,
    beforeUnmount: truncateMiddleBeforeUnmount,
  })

  app.directive(DND_DIRECTIVE, {
    mounted: (el: HTMLElement, binding: DirectiveBinding, node) => {
      const counter = ref(0)
      el.addEventListener("dragover", dndDragOverHandler, false)
      el.addEventListener("dragenter", (ev) => dndDragEnterHandler(ev, el, counter))
      el.addEventListener("dragleave", () => dragLeaveHandler(el, counter))
      el.addEventListener("drop", (ev) => dndDropHandler(ev, el, node, counter))
    },
  })

  // USE WITH CAUTION!
  // Functions similar to v-show with the difference being that instead of doing "display: none",
  // it sets the visibility to hidden and position to fixed. This way the element is still rendered,
  // but it won't really take space in the DOM layout. This is hacky solution that enables the "truncate" directive
  // to work properly. Additionally, if the value is initially set to false, it would set the display to none.
  app.directive(VISIBLE_DIRECTIVE, {
    beforeMount: visibleBeforeMount,
    updated: visibleUpdated,
  })

  app.directive(CLICK_OUTSIDE_DIRECTIVE, {
    beforeMount: clickOutsideBeforeMount,
    beforeUnmount: clickOutsideBeforeUnmount,
  })
})

//region Truncate Middle

let canvasCtx: CanvasRenderingContext2D | null = null
const TRUNCATE_MIDDLE_DIRECTIVE = "truncate-middle"
const TRUNCATE_MIDDLE_CLASS = "truncate-middle"
const TRUNCATE_MIDDLE_ID = "truncate-id"
const truncateMiddleMap = new Map<string, TruncateMiddleObject>()

type TruncateMiddleObject = {
  text: string
  el: HTMLElement
}

window.addEventListener("resize", () => {
  if (truncateMiddleMap.size === 0) return

  for (const value of truncateMiddleMap.values()) {
    if (document.contains(value.el)) {
      setTruncatedText(value.text, value.el)
    }
  }
})

function truncateMiddleMount(el: HTMLElement, binding: DirectiveBinding) {
  // Only execute if there are is some text
  if (!binding.value) return

  const text: string = binding.value.toString()

  const textValueSpan = document.createElement("span")

  textValueSpan.classList.add(TRUNCATE_MIDDLE_CLASS)
  textValueSpan.classList.add("flex")

  // We add some unique identifier so that we can use the truncateMiddleMap
  const uuid = uuid4()
  textValueSpan.setAttribute(TRUNCATE_MIDDLE_ID, uuid)
  truncateMiddleMap.set(uuid, { el: textValueSpan, text })
  el.append(textValueSpan)

  setTruncatedText(text, textValueSpan)
}

function truncateMiddleBeforeUpdate(el: HTMLElement, binding: DirectiveBinding) {
  // Only execute if there are is some new text to update
  if (!binding.value || binding.value === binding.oldValue) return

  const text: string = binding.value.toString()

  const selector = `[${TRUNCATE_MIDDLE_ID}]`
  const textValueSpan = el.querySelector(selector)

  if (textValueSpan) {
    setTruncatedText(text, textValueSpan as HTMLElement)
  }
}
function truncateMiddleBeforeUnmount(el: HTMLElement) {
  const selector = `[${TRUNCATE_MIDDLE_ID}]`
  const textValueSpan = el.querySelector(selector)
  const id = textValueSpan?.getAttribute(TRUNCATE_MIDDLE_ID)
  if (id) truncateMiddleMap.delete(id)
}

function setTruncatedText(text: string, el: HTMLElement) {
  const textWidth = getTextWidth(text, el)
  // So we can ensure that we are always going to fit in the box, and it is not too close
  const extraPadding = 15

  // Text is long enough to fit in without truncation
  if (textWidth + extraPadding < el.offsetWidth) {
    el.innerHTML = text
    return
  }

  const averageCharLength = textWidth / text.length
  const shouldRemoveCharSpacing = (textWidth - el.offsetWidth + extraPadding) / averageCharLength
  const charsPerSide = Math.floor((text.length - shouldRemoveCharSpacing) / 2)
  const leftText = (text.slice(0, charsPerSide) || "").trim()
  const rightText = (text.slice(-charsPerSide) || "").trim()

  el.innerHTML = leftText + "..." + rightText
}

function getTextWidth(text: string, el: Element) {
  const context = getCanvas()
  if (!context) return 0

  context.font = getCanvasFont(el)
  const metrics = context.measureText(text)
  return metrics.width
}

function getCanvas() {
  // re-use canvas object for better performance
  if (!canvasCtx) {
    const canvas = document.createElement("canvas")
    canvasCtx = canvas.getContext("2d")
  }

  return canvasCtx
}

function getCSSStyle(element: Element, prop: string) {
  return window.getComputedStyle(element, null).getPropertyValue(prop)
}

function getCanvasFont(el: Element = document.body) {
  // Setting some default values just in case, although the document.body should suffice
  const fontWeight = getCSSStyle(el, "font-weight") || "normal"
  const fontSize = getCSSStyle(el, "font-size") || "16px"
  const fontFamily = getCSSStyle(el, "font-family") || "Roboto"

  return `${fontWeight} ${fontSize} ${fontFamily}`
}

//endregion

//region Drag and Drop

const DND_CLASS = "drag-and-drop"
const DND_DIRECTIVE = "dnd"
const DND_EMIT = "dnd"

function stopAndPreventDrag(ev: DragEvent) {
  if (ev && ev.dataTransfer && (ev.dataTransfer.dropEffect = "copy")) {
    ev.preventDefault()
    ev.stopPropagation()
  }
}

function dndDragOverHandler(ev: DragEvent) {
  // Prevent default behavior (Prevent file from being opened)
  stopAndPreventDrag(ev)
}

function dndDragEnterHandler(ev: DragEvent, el: HTMLElement, counter: Ref<number>) {
  stopAndPreventDrag(ev)
  counter.value++

  if (!el?.classList.contains(DND_CLASS)) {
    el?.classList.add(DND_CLASS)
  }
}

function dragLeaveHandler(el: HTMLElement, counter: Ref<number>) {
  counter.value--

  if (counter.value === 0 && el.classList.contains(DND_CLASS)) {
    el.classList.remove(DND_CLASS)
  }
}

function dndDropHandler(ev: DragEvent, el: HTMLElement, node: VNode, counter: Ref<number>) {
  // Prevent default behavior (Prevent file from being opened)
  stopAndPreventDrag(ev)

  el.classList.remove(DND_CLASS)
  counter.value = 0

  if (!ev.dataTransfer) {
    showDragAndDropFail()
    return
  }

  if (ev.dataTransfer.files.length > 0) {
    const files: File[] = []

    for (const file of ev.dataTransfer.files) {
      // Ignore empty files. Files that are caught mid-stream also show up as size 0, and we don't want them.
      if (file.size > 0) {
        files.push(file)
      }
    }

    if (files.length !== ev.dataTransfer.files.length) {
      showDragAndDropEmptyFiles()
    } else if (files.length > 0) {
      dndEmitFiles(node, files)
    } else {
      showDragAndDropFail()
    }
  } else {
    showDragAndDropFail()
  }
}

function dndEmitFiles(node: VNode, files: File[]) {
  if (node.component) {
    node.component.emit(DND_EMIT, { detail: files })
  } else {
    const el = node.el as HTMLElement
    el.dispatchEvent(new CustomEvent(DND_EMIT, { detail: files }))
  }
}

//endregion

//region Visible Element

const VISIBLE_DIRECTIVE = "visible"

function visibleBeforeMount(el: HTMLElement, binding: DirectiveBinding) {
  const shouldRender = Boolean(binding.value)
  if (shouldRender) {
    setElVisibility(el, shouldRender)
  } else {
    // The element does not initially have a render value, so don't even bother
    el.style.display = "none"
  }
}

function visibleUpdated(el: HTMLElement, binding: DirectiveBinding) {
  if (binding.value === binding.oldValue) return

  setElVisibility(el, Boolean(binding.value))
}

function setElVisibility(el: HTMLElement, isVisible: boolean) {
  if (isVisible) {
    el.style.display = ""
    el.style.position = ""
    el.style.visibility = ""
  } else {
    el.style.display = "block"
    el.style.position = "fixed"
    el.style.visibility = "hidden"
  }
}

//endregion

//region Click Outside

const CLICK_OUTSIDE_DIRECTIVE = "click-outside"

interface ExtendedHTMLElement extends HTMLElement {
  clickOutside: (event: MouseEvent) => void
}

function clickOutsideBeforeMount(el: ExtendedHTMLElement, binding: DirectiveBinding) {
  el.clickOutside = function (event: MouseEvent) {
    if (event.target instanceof Element && !(el === event.target || el.contains(event.target))) {
      // Check if the clicked element is neither the element
      // to which the directive is applied nor its child
      // Invoke the provided method
      binding.value(event)
    }
  }
  document.addEventListener("click", el.clickOutside)
}

function clickOutsideBeforeUnmount(el: ExtendedHTMLElement) {
  // Remove the event listener when the bound element is unmounted
  document.removeEventListener("click", el.clickOutside)
}

//endregion
